import React from "react";

import { ChevronsUpDown } from "lucide-react";

import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { CHILEAN_COMUNAS, CHILEAN_REGIONS } from "~/constants/places";

interface RegionComunaSelectorProps {
  selectedRegion: string;
  setSelectedRegion: (region: string) => void;
  selectedComuna: string;
  setSelectedComuna: (comuna: string) => void;
  availableComunas: { id: string; name: string }[];
}

const RegionComunaSelector: React.FC<RegionComunaSelectorProps> = ({
  selectedRegion,
  setSelectedRegion,
  selectedComuna,
  setSelectedComuna,
  availableComunas = [],
}) => {
  // Get comunas for the selected region
  const comunasForRegion = (CHILEAN_COMUNAS || []).filter(
    (c) => c.region === selectedRegion
  );

  const handleRegionChange = (regionId: string) => {
    setSelectedRegion(regionId);
    setSelectedComuna(""); // Reset comuna when region changes
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormItem>
        <FormLabel>
          Región <span className="text-red-500">*</span>
        </FormLabel>
        <FormControl>
          <div className="relative">
            <select
              value={selectedRegion}
              onChange={(e) => {
                handleRegionChange(e.target.value);
              }}
              className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Seleccionar región</option>
              {(CHILEAN_REGIONS || [])
                .sort((a, b) => {
                  if (a.value === "metropolitana") return -1;
                  if (b.value === "metropolitana") return 1;
                  return a.label.localeCompare(b.label);
                })
                .map((region) => (
                  <option key={region.value} value={region.value}>
                    {region.label}
                  </option>
                ))}
            </select>
            <ChevronsUpDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground opacity-60" />
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
      <FormItem>
        <FormLabel>
          Comuna <span className="text-red-500">*</span>
        </FormLabel>
        <FormControl>
          <div className="relative">
            <select
              value={selectedComuna}
              onChange={(e) => {
                setSelectedComuna(e.target.value);
              }}
              disabled={!selectedRegion}
              className="flex h-10 w-full appearance-none items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">
                {selectedRegion
                  ? "Seleccionar comuna"
                  : "Seleccione región primero"}
              </option>
              {comunasForRegion
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((comuna) => (
                  <option key={comuna.value} value={comuna.value}>
                    {comuna.label}
                  </option>
                ))}
            </select>
            <ChevronsUpDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground opacity-60" />
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </div>
  );
};

export default RegionComunaSelector;
